// src/components/TimeSheetApproval.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Button, Nav, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/Timesheet.css";

import Axios from "../utils/Axios";
import Toast from "../utils/Toast";
import AuthHandler from "../utils/AuthHandler";
import { navigate } from "../utils/navigation";
import { mergeDaysWithWeek } from "../utils/dateMaker";

import {
  EMP_WEEK_RECORD,
  GET_EACH_EMP_TIMESHEET,
  REVIEW_TIMESHEET,
} from "../config/API_Collection";

export default function TimeSheetApproval({
  setTimeSheetApro,
  weekProp,
  emp_id ,
  // 👇 NEW: callback provided by parent MyProfile
  onRefreshAllTimesheets,
}) {
  // State to track expanded/collapsed job groups
  const [expandedGroups, setExpandedGroups] = useState({});

  // Toggle expand/collapse for a group
  const toggleGroup = (projectNumber) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [projectNumber]: !prev[projectNumber],
    }));
  };
  const [activeDay, setActiveDay] = useState("Friday");
  const [weekData, setWeekData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [checkUpdate, setCheckUpdate] = useState(false);
  const [filterJobNumber, setFilterJobNumber] = useState(""); // Filter state

  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [locked, setLocked] = useState(false); // permanently disable after success
  const [needsRefresh, setNeedsRefresh] = useState(false); // mark that we must refetch on close

  const [toast, setToast] = useState({ message: "", type: "", key: 0 });
  const showToast = (message, type) =>
    setToast({ message, type, key: Date.now() });

  const [showReject, setShowReject] = useState(false);
  const [rejectComment, setRejectComment] = useState("");

  const baseStatus = weekProp?.status ?? null; // 0,1,2,3

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        if (emp_id) {
          const r = await Axios.get(
            `${GET_EACH_EMP_TIMESHEET}week_start=${weekProp?.week_start}&emp_id=${emp_id}`
          );
          if (r.status === 200) setWeekData(r.data?.data?.[0] || {});
        } else {
          const r = await Axios.get(`${EMP_WEEK_RECORD}${weekProp?.week_start}`);
          if (r.status === 200) setWeekData(r.data?.data?.[0] || {});
        }
      } catch (err) {
        AuthHandler(err?.response?.status, setToast, navigate);
        showToast("Failed to load timesheet", "failed");
      } finally {
        setIsLoading(false);
      }
    };
    if (weekProp?.week_start) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekProp?.week_start, emp_id, checkUpdate]);

  const daysBase = useMemo(
    () => [
      { d: 0, key: "Monday", label: "Monday", date: "19th Aug" },
      { d: 1, key: "Tuesday", label: "Tuesday", date: "20th Aug" },
      { d: 2, key: "Wednesday", label: "Wednesday", date: "21st Aug" },
      { d: 3, key: "Thursday", label: "Thursday", date: "22nd Aug" },
      { d: 4, key: "Friday", label: "Friday", date: "23rd Aug" },
      { d: 5, key: "Saturday", label: "Saturday", date: "24th Aug" },
      { d: 6, key: "Sunday", label: "Sunday", date: "25th Aug" },
    ],
    []
  );

  const days = useMemo(
    () =>
      mergeDaysWithWeek(
        daysBase,
        weekProp?.week_start || weekData?.week_start || ""
      ),
    [daysBase, weekProp?.week_start, weekData?.week_start]
  );

  useEffect(() => {
    if (days.length === 7) {
      const todayIso = new Date().toLocaleDateString("en-CA");
      const match = days.find((d) => d.iso === todayIso);
      if (match) setActiveDay(match.key);
    }
  }, [days]);

  const activeIso = useMemo(
    () => days.find((d) => d.key === activeDay)?.iso,
    [days, activeDay]
  );

  // Helper function to get holiday/leave info from week_day_list
  const getDayInfo = (iso) => {
    if (!Array.isArray(weekData?.week_day_list)) return null;
    for (let dayObj of weekData.week_day_list) {
      if (dayObj[iso]) {
        return dayObj[iso];
      }
    }
    return null;
  };

  const rowsForActiveDay = useMemo(() => {
    const tasks = Array.isArray(weekData?.tasks) ? weekData.tasks : [];
    return tasks.map((t) => {
      const day = (t.days || []).find((x) => x?.date === activeIso) || {};
      const hours =
        typeof day.hours === "number" ? day.hours : Number(day.hours) || 0;
      return {
        task_assignment_id: t.task_assignment_id,
        task_name: t.task_name,
        task_id: t.task_id,
        project_name: t.project_name,
        project_number: t.project_number,
        hours,
        comment: day.comment || "",
      };
    });
  }, [weekData?.tasks, activeIso]);

  // Filter tasks by job number
  const filteredRows = useMemo(() => {
    if (!filterJobNumber.trim()) return rowsForActiveDay;
    
    const searchTerm = filterJobNumber.toLowerCase().trim();
    return rowsForActiveDay.filter((row) => {
      const jobNumber = (row?.project_number || "").toLowerCase();
      const taskName = (row?.task_name || "").toLowerCase();
      return jobNumber.includes(searchTerm) || taskName.includes(searchTerm);
    });
  }, [rowsForActiveDay, filterJobNumber]);

  // Group tasks by project number
  const groupedTasks = useMemo(() => {
    const groups = {};
    filteredRows.forEach((row) => {
      const key = row.project_number || "No Project";
      if (!groups[key]) {
        groups[key] = {
          project_number: row.project_number,
          project_name: row.project_name,
          tasks: [],
        };
      }
      groups[key].tasks.push(row);
    });
    return Object.values(groups);
  }, [filteredRows]);

  // Ensure all groups are expanded by default when groupedTasks changes
  useEffect(() => {
    if (groupedTasks && groupedTasks.length > 0) {
      const allExpanded = {};
      groupedTasks.forEach(g => { allExpanded[g.project_number] = true; });
      setExpandedGroups(allExpanded);
    }
  }, [groupedTasks]);

  const dayTotals = useMemo(() => {
    const tasks = Array.isArray(weekData?.tasks) ? weekData.tasks : [];
    return days.map((d) => {
      let sum = 0;
      tasks.forEach((t) => {
        const x = (t.days || []).find((y) => y?.date === d.iso);
        const n = x
          ? typeof x.hours === "number"
            ? x.hours
            : Number(x.hours) || 0
          : 0;
        if (!isNaN(n)) sum += n;
      });
      return sum;
    });
  }, [weekData?.tasks, days]);

  const weekTotal = useMemo(
    () => dayTotals.reduce((a, b) => a + b, 0),
    [dayTotals]
  );

  const timesheetId =
    weekProp?.timesheet_id ??
    weekData?.timesheet_id ??
    weekData?.id ??
    weekProp?.id;

  const baseDisabled =
    isLoading || !timesheetId || locked || baseStatus === 2 || baseStatus === 3;
  const approveDisabled = baseDisabled || approving || rejecting;
  const rejectDisabled = baseDisabled || approving || rejecting;

  const handleApprove = async () => {
    if (approveDisabled) return;
    try {
      setApproving(true);
      const res = await Axios.post(REVIEW_TIMESHEET, {
        status: 2,
        review_comments: "Approved by reviewer",
        timesheet_id: timesheetId,
      });
      if (res?.status === 200 || res?.status === 201) {
        showToast("Timesheet approved successfully", "success");
        setLocked(true);
        setNeedsRefresh(true);       // mark for parent refresh on close
        setCheckUpdate((v) => !v);   // local refresh
      } else {
        showToast(res?.data?.message || "Failed to approve timesheet", "failed");
        setCheckUpdate((v) => !v);
      }
    } catch (err) {
      AuthHandler(err?.response?.status, setToast, navigate);
      showToast(
        err?.response?.data?.message || err?.message || "Failed to approve timesheet",
        "failed"
      );
    } finally {
      setApproving(false);
    }
  };

  const openReject = () => {
    if (rejectDisabled) return;
    setRejectComment("");
    setShowReject(true);
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (rejectDisabled) return;
    const note = (rejectComment || "").trim();
    if (!note) {
      showToast("Please enter a comment to reject the timesheet.", "failed");
      return;
    }
    try {
      setRejecting(true);
      const res = await Axios.post(REVIEW_TIMESHEET, {
        status: 3,
        review_comments: note,
        timesheet_id: timesheetId,
      });
      if (res?.status === 200 || res?.status === 201) {
        showToast("Timesheet rejected successfully", "success");
        setLocked(true);
        setShowReject(false);
        setNeedsRefresh(true);       // mark for parent refresh on close
        setCheckUpdate((v) => !v);   // local refresh
      } else {
        showToast(res?.data?.message || "Failed to reject timesheet", "failed");
      }
    } catch (err) {
      AuthHandler(err?.response?.status, setToast, navigate);
      showToast(
        err?.response?.data?.message || err?.message || "Failed to reject timesheet",
        "failed"
      );
    } finally {
      setRejecting(false);
    }
  };

  // 👇 UPDATED: when Close is clicked, if we've approved/rejected, call parent's refresh
  const handleClose = () => {
    if (needsRefresh && typeof onRefreshAllTimesheets === "function") {
      onRefreshAllTimesheets();
    }
    setTimeSheetApro(false);
  };

  return (
    <>
      <Modal show size="xl" centered backdrop="static" keyboard={false}>
        <style>{`
          .no-spinners::-webkit-outer-spin-button,
          .no-spinners::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
          .no-spinners { -moz-appearance: textfield; }

          .modal-backdrop-dark-blur.show {
            background-color: rgba(0,0,0,0.75);
            backdrop-filter: blur(3px);
          }
        `}</style>

        <Modal.Header className="border-0 px-4 pt-4 pb-2 justify-content-between align-items-center timesheet-body timesheet-y">
          <h4 className="timesheet-title">Time Sheet</h4>
          <h6 className="timesheet-date">{weekProp?.week_date}</h6>
        </Modal.Header>
        <div className="divider title-margin" />

        <Modal.Body className="px-4 timesheet-body">
          <div className="position-relative">
            {isLoading && (
              <div className="loading-overlay">
                <Spinner animation="border" role="status" />
                <span className="ms-2">Loading…</span>
              </div>
            )}
            <Toast key={toast.key} message={toast.message} type={toast.type} />

            <div className={isLoading ? "opacity-50 pe-none" : ""}>
              <div className="d-flex justify-content-between align-items-end mb-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="fw-semibold" style={{ whiteSpace: "nowrap" }}>Allocated Tasks</div>
                  <Form.Control
                    type="text"
                    placeholder="Filter by Job number"
                    className="filter-input"
                    style={{ width: "250px", height: "38px" }}
                    value={filterJobNumber}
                    onChange={(e) => setFilterJobNumber(e.target.value)}
                  />
                </div>

                <Nav variant="tabs" activeKey={activeDay} className="timesheet-tabs">
                  {days.map((day) => {
                    const dayInfo = getDayInfo(day.iso);
                    let specialText = null;
                    let tabBg = {};
                    if (dayInfo?.isHoliday) {
                      specialText = "Public Holiday";
                    } else if (dayInfo?.isLeave) {
                      specialText = "Paid Leave";
                    }
                    return (
                      <Nav.Item key={day.key}>
                        <Nav.Link eventKey={day.key} onClick={() => setActiveDay(day.key)} style={tabBg}>
                          <div className="day-tab">
                            <div style={{ color: (dayInfo?.isHoliday || dayInfo?.isLeave) ? '#bfc2c7' : undefined }}>{day.label}</div>
                            <small style={{ color: (dayInfo?.isHoliday || dayInfo?.isLeave) ? '#bfc2c7' : undefined }}>{day.date}</small>
                            {specialText && (
                              <div style={{ color: '#ffc107', fontSize: '0.8em', marginTop: 2 }}>{specialText}</div>
                            )}
                          </div>
                        </Nav.Link>
                      </Nav.Item>
                    );
                  })}
                </Nav>
              </div>

              <div className="card p-3 border rounded-3">
                <table className="table table-borderless align-middle mb-0">
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="3">
                          <div className="placeholder-glow">
                            <span className="placeholder col-7" />
                            <span className="placeholder col-4" />
                            <span className="placeholder col-5" />
                          </div>
                        </td>
                      </tr>
                    ) : (() => {
                      const dayInfo = getDayInfo(activeIso);
                      if (dayInfo?.isHoliday || dayInfo?.isLeave) {
                        const label = dayInfo?.isHoliday ? 'Public Holiday' : 'Paid Leave';
                        const comment = dayInfo?.comment || (dayInfo?.isHoliday ? '-' : '-');
                        return (
                          <>
                            <tr style={{ background: '#f5f6f8' }}>
                              <td style={{ width: "40%", paddingLeft: "2rem", fontWeight: 'bold', color: '#222' }}>{label}</td>
                              <td style={{ width: "20%" }}>
                                <Form.Control type="number" value={8} readOnly tabIndex={-1} style={{ background: '#e9ecef', color: '#222', fontWeight: 'bold', fontSize: '1.05em' }} className="time-input no-spinners" />
                              </td>
                              <td style={{ width: "40%" }}>
                                <Form.Control type="text" value={comment} readOnly tabIndex={-1} style={{ background: '#e9ecef', color: '#222', fontWeight: 'bold', fontSize: '1.05em' }} className="comment-input" />
                              </td>
                            </tr>
                            {groupedTasks.map((group, groupIdx) => (
                              <React.Fragment key={group.project_number}>
                                <tr>
                                  <td colSpan="3" className="py-2">
                                    <span
                                      style={{ fontWeight: "bold", cursor: "pointer", userSelect: "none" }}
                                      onClick={() => toggleGroup(group.project_number)}
                                    >
                                      {expandedGroups[group.project_number] ? (
                                        <span style={{ marginRight: 8 }}>▼</span>
                                      ) : (
                                        <span style={{ marginRight: 8 }}>▶</span>
                                      )}
                                      {group.project_number} - {group.project_name}
                                    </span>
                                  </td>
                                </tr>
                                {expandedGroups[group.project_number] && group.tasks.map((row) => (
                                  <tr key={row.task_assignment_id}>
                                    <td style={{ width: "40%", paddingLeft: "2rem" }}>{row.task_name}</td>
                                    <td style={{ width: "20%" }}>
                                      <Form.Control type="number" value={row.hours === 0 ? "" : row.hours} readOnly tabIndex={-1} style={{ background: '#e9ecef', color: '#222', pointerEvents: 'none', boxShadow: 'none', borderColor: '#dee2e6' }} className="time-input no-spinners" />
                                    </td>
                                    <td style={{ width: "40%" }}>
                                      <Form.Control type="text" value={row.comment} readOnly tabIndex={-1} style={{ background: '#e9ecef', color: '#222', pointerEvents: 'none', boxShadow: 'none', borderColor: '#dee2e6' }} className="comment-input" />
                                    </td>
                                  </tr>
                                ))}
                                {groupIdx < groupedTasks.length - 1 && (
                                  <tr>
                                    <td colSpan="3" style={{ borderBottom: "2px solid #e0e0e0", padding: 0 }} />
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </>
                        );
                      }
                      // Default: normal rendering
                      return groupedTasks.length > 0 ? (
                        [...groupedTasks].sort((a, b) => String(a.project_number).localeCompare(String(b.project_number))).map((group, groupIdx) => (
                          <React.Fragment key={group.project_number}>
                            {/* Project Header Row (bold, no background, with expand/collapse icon) */}
                            <tr>
                              <td colSpan="3" className="py-2">
                                <span
                                  style={{ fontWeight: "bold", cursor: "pointer", userSelect: "none" }}
                                  onClick={() => toggleGroup(group.project_number)}
                                >
                                  {expandedGroups[group.project_number] ? (
                                    <span style={{ marginRight: 8 }}>▼</span>
                                  ) : (
                                    <span style={{ marginRight: 8 }}>▶</span>
                                  )}
                                  {group.project_number} - {group.project_name}
                                </span>
                              </td>
                            </tr>
                            {/* Task Rows (normal text, only if expanded) */}
                            {expandedGroups[group.project_number] && group.tasks.map((row) => {
                              const hoursVal = row.hours === 0 ? "" : row.hours;
                              return (
                                <tr key={row.task_assignment_id}>
                                  <td style={{ width: "40%", paddingLeft: "2rem", color: '#212529', fontWeight: 'normal' }}>
                                    <span>{row.task_name}</span>
                                  </td>
                                  <td style={{ width: "20%" }}>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      step="0.25"
                                      placeholder="00:00"
                                      className="time-input no-spinners"
                                      value={hoursVal}
                                      disabled
                                      readOnly
                                    />
                                  </td>
                                  <td style={{ width: "40%" }}>
                                    <div className="d-flex align-items-start flex-column">
                                      <div className="w-100 d-flex align-items-center">
                                        <Form.Control
                                          type="text"
                                          placeholder="Comment"
                                          className="comment-input"
                                          value={row.comment}
                                          disabled
                                          readOnly
                                        />
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Spacing between groups */}
                            {groupIdx < groupedTasks.length - 1 && (
                              <tr style={{ height: "8px" }}>
                                <td colSpan="3" className="p-0" />
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-muted">
                            {rowsForActiveDay.length > 0 ? "No tasks match your filter" : "No Data Available"}
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>

                <div className="totals-row d-flex justify-content-between mt-3">
                  <div className="fw-semibold">Total Per Day</div>
                  <div className="d-flex gap-4 flex-wrap">
                    {days.map((d, idx) => (
                      <span
                        key={d.key}
                        className={`fw-semibold ${activeDay === d.key ? "text-primary" : "text-dark"}`}
                        title={`${d.label} • ${d.date}`}
                      >
                        {dayTotals[idx].toFixed(2)} hrs
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 px-4 justify-content-between timesheet-body timesheet-b mt-5">
          <div>
            <Button
              variant="primary"
              className="me-2"
              onClick={handleApprove}
              disabled={approveDisabled}
            >
              {approving ? "Approving…" : "Approve Time sheet"}
            </Button>

            <Button
              variant="secondary"
              className="me-2"
              onClick={openReject}
              disabled={rejectDisabled}
            >
              Reject Time sheet
            </Button>

            <Button
              className="cancel-btn btn-secondary"
              onClick={handleClose}
              disabled={approving || rejecting}
            >
              Close
            </Button>
          </div>
          <div className="fw-semibold">Week Total: {weekTotal.toFixed(2)} hrs</div>
        </Modal.Footer>
      </Modal>

      {/* Reject modal */}
      <Modal
        show={showReject}
        centered
        backdrop="static"
        onHide={() => setShowReject(false)}
        backdropClassName="modal-backdrop-dark-blur"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Reject Timesheet</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleRejectSubmit}>
          <Modal.Body>
            <Form.Group controlId="rejectComments">
              <Form.Label>Reason / comments (required)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Provide the reason for rejection"
                required
                disabled={rejecting || approving}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" type="submit" disabled={rejecting || approving}>
              {rejecting ? "Rejecting…" : "Reject Time sheet"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowReject(false)}
              disabled={rejecting || approving}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
